import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Logger } from '@mopla/logger';
import {
	cancelPurchaseMutation,
	findMostRelevantTicketQueryKey,
	getTicketDetailQueryKey,
	listMyTicketsQueryKey,
} from '@mopla/ticketshop-api';

import { usePassengerAppNavigate } from '../../../../hooks';

export type TMyTicketCancellationStep =
	| 'idle'
	| 'confirm'
	| 'success'
	| 'error';

interface IUseMyTicketCancellationResult {
	step: TMyTicketCancellationStep;
	isPending: boolean;
	openConfirmation: () => void;
	abortCancellation: () => void;
	confirmCancellation: () => Promise<void>;
	closeSuccessModal: () => void;
	closeErrorModal: () => void;
}

/**
 * Cancellation flow for a purchased ticket: POST /api/v1/purchases/{purchaseId}/cancel.
 * A 204 (no body) means the preorder was cancelled.
 * Nothing has been billed at that point, so there is no refund involved.
 */
export const useMyTicketCancellation = (
	purchaseId?: string
): IUseMyTicketCancellationResult => {
	const queryClient = useQueryClient();
	const { navigateBack } = usePassengerAppNavigate();

	const [step, setStep] = useState<TMyTicketCancellationStep>('idle');
	const { mutateAsync, isPending } = useMutation(cancelPurchaseMutation());

	const openConfirmation = useCallback(() => setStep('confirm'), []);
	const abortCancellation = useCallback(() => setStep('idle'), []);
	/** Failed attempt: back to the detail view, from where the user can retry. */
	const closeErrorModal = useCallback(() => setStep('idle'), []);

	/** Cancellation done — leave the detail page. */
	const closeSuccessModal = useCallback(() => {
		setStep('idle');
		navigateBack();
	}, [navigateBack]);

	const confirmCancellation = useCallback(async () => {
		if (!purchaseId || isPending) {
			return;
		}

		try {
			await mutateAsync({ path: { purchaseId } });
		} catch (e) {
			// Lazily resolve the logger — it is initialized during app bootstrap.
			Logger.getInstance('Ticketshop').error(
				'[ticketshop] cancellation failed',
				e
			);
			setStep('error');

			return;
		}

		setStep('success');

		// The 204 carries no body, so the cancelled state only shows up on a
		// refetch: Ticket Detail page (status → "cancelled"), Tickets tab,
		// and the Home "next ticket" card, which must not keep advertising
		// the cancelled preorder. A failed refresh does not undo the cancellation,
		// so it must not turn the success modal into the error one.
		try {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: getTicketDetailQueryKey({ path: { purchaseId } }),
				}),
				queryClient.invalidateQueries({ queryKey: listMyTicketsQueryKey() }),
				queryClient.invalidateQueries({
					queryKey: findMostRelevantTicketQueryKey(),
				}),
			]);
		} catch (e) {
			Logger.getInstance('Ticketshop').error(
				'[ticketshop] refresh after cancellation failed',
				e
			);
		}
	}, [purchaseId, isPending, mutateAsync, queryClient]);

	return {
		step,
		isPending,
		openConfirmation,
		abortCancellation,
		confirmCancellation,
		closeSuccessModal,
		closeErrorModal,
	};
};
